import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Stack, Tooltip } from '@mui/material';
import React from 'react';
import Investor from '../../models/Investor';
import { BaseModelCard } from '../styles/ModelCard';
import CardViewLinks, { revealCardViewLinksOnHoverSx } from './CardViewLinks';
import { StyledTypography } from '../styles/styles';

export class AdminModelCard extends BaseModelCard {
  protected shouldShowOverlay(): boolean {
    return !this.props.viewLinks;
  }

  protected renderInlineActions(): React.ReactNode {
    return <CardViewLinks hrefs={this.props.viewLinks} />;
  }

  protected inlineActionsSx(): Record<string, unknown> {
    return this.props.viewLinks ? revealCardViewLinksOnHoverSx : {};
  }

  protected renderOverlay(): React.ReactNode {
    const {
      name,
      entity,
      onPromote,
      onDemote,
      onDelete,
      promotionStageLabel,
      demotionStageLabel,
      actionButtons,
      hideViewButtons,
    } = this.props;

    // Extract contact info
    const contactName = name || 'Unnamed';
    let role: string | undefined;
    let representativeType: string | undefined;
    const fundOpportunityTitle = (entity as Investor)?.fundOpportunity?.name;
    const fundOpportunityExternalId = this.getFundOpportunityExternalId();

    const isInvestmentEntityContact =
      entity && typeof entity === 'object' && 'contact' in entity && 'role' in entity;
    if (isInvestmentEntityContact) {
      const investmentEntityContact = entity as any;
      role = investmentEntityContact.role;
      representativeType = investmentEntityContact.representativeType;
    }

    return (
      <>
        {/* Fund Opportunity Title - if provided */}
        <StyledTypography
          sx={{
            color: 'white',
            fontSize: '0.6rem',
            fontWeight: 700,
            marginBottom: '4px',
            textAlign: 'center',
          }}
        >
          {fundOpportunityTitle ? `${fundOpportunityTitle} - ${contactName}` : contactName}
        </StyledTypography>
        {role && (
          <StyledTypography
            sx={{
              color: 'white',
              fontSize: '0.5rem',
              opacity: 0.8,
              marginBottom: '4px',
              textAlign: 'center',
            }}
          >
            {role}
            {representativeType && ` - ${representativeType}`}
          </StyledTypography>
        )}
        <Stack direction="row" spacing={0.5}>
          {!hideViewButtons && (
            <>
              <Tooltip title="View Investor">
                <IconButton
                  onClick={this.handleNavigateInvestor}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: 'rgba(0,0,0,0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                      color: 'rgba(0,0,0,1)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '0.7rem',
                    },
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Contact">
                <IconButton
                  onClick={this.handleNavigateContact}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: 'rgba(0,0,0,0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                      color: 'rgba(0,0,0,1)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '0.7rem',
                    },
                  }}
                >
                  <PersonIcon />
                </IconButton>
              </Tooltip>
              {fundOpportunityExternalId && (
                <Tooltip title="View Fund">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      this.handleNavigateFund();
                    }}
                    sx={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'rgba(0,0,0,0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        color: 'rgba(0,0,0,1)',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.7rem',
                      },
                    }}
                  >
                    <ApartmentIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {/* Use custom action buttons if provided, otherwise use default promote/demote buttons */}
          {actionButtons ? (
            actionButtons
          ) : (
            <>
              {promotionStageLabel && onPromote && (
                <Tooltip title={promotionStageLabel}>
                  <IconButton
                    onClick={onPromote}
                    sx={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'rgba(0,0,0,0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        color: 'rgba(0,0,0,1)',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.7rem',
                      },
                    }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Tooltip>
              )}
              {demotionStageLabel && onDemote && (
                <Tooltip title={demotionStageLabel}>
                  <IconButton
                    onClick={onDemote}
                    sx={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'rgba(0,0,0,0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        color: 'rgba(0,0,0,1)',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.7rem',
                      },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    onClick={onDelete}
                    sx={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'rgba(0,0,0,0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        color: 'rgba(0,0,0,1)',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.7rem',
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Stack>
      </>
    );
  }
}

export default AdminModelCard;
